 import { AdminPanelSettings, ControlPoint, Dataset, ExpandMore, Layers, LayersOutlined, Update, VpnKey, Person, Notifications, Logout } from "@mui/icons-material";
import { Divider, Drawer, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { useState, useEffect } from "react";
import { matchRoutes, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useGetRunsCountForWorkflow } from "../api/workflowsApi";
import { useWorkflowId } from "../utils/misc";
import customFetch from "../utils/customFetch";
import { useAuth } from "../context/AuthContext";
import clsx from "clsx";

const useMe = () => {
    const [me, setMe] = useState<{email: string, name: string} | null>(null);
    useEffect(() => {
        customFetch('/v1/me').then(async (r) => {
            if (!r.ok) return;
            const j = await r.json();
            setMe({ email: j.email, name: j.name });
        });
    }, []);
    return me;
}

const Navbar = () => {
    const [open, setOpen] = useState(true);
    const [userOpen, setUserOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const workflowId = useWorkflowId();
    const {data : runsAggregation } = useGetRunsCountForWorkflow();
    const runCount = runsAggregation?.total || 0;
    const me = useMe();
    const { logout } = useAuth();

    const isWorkflowOpen =
        !!matchRoutes(
            [{ path: '/workflow/:path/:workflowId' }],
            location
        );

    return (
        <Drawer 
            variant="permanent" 
            open={open} 
            className={clsx("transition-all duration-300", open ? 'w-64' : 'w-16')}
            PaperProps={{
                className: clsx(
                    "bg-white border-r border-borderGrey transition-all duration-300",
                    open ? 'w-64' : 'w-16'
                ),
                elevation: 0,
                sx: {
                    boxShadow: '2px 0 8px rgba(0, 0, 0, 0.04)',
                }
            }}
        >
            <div className="relative h-full flex flex-col">
                {/* Logo Section */}
                <div className="px-4 py-5 border-b border-borderGrey flex-shrink-0">
                    <NavLink 
                        to="/"
                        className="block transition-opacity duration-200 hover:opacity-80"
                    >
                        {open ? (
                            <img src="/turing.png" alt="Turing Logo" className="h-8 transition-all duration-300"/>
                        ) : (
                            <img src="/nanonets.png" alt="Nanonets Logo" className="w-6 h-6 mx-auto transition-all duration-300"/>
                        )}
                    </NavLink>
                </div>

                {/* Navigation Items */}
                <div className="flex-1 overflow-y-auto py-2 min-h-0 pb-20">
                    <NavLink
                        to="/dashboard"
                        end
                        className={({ isActive }) => clsx(
                            "block relative transition-all duration-200",
                            isActive && "bg-backgroundBlue"
                        )}
                    >
                        {({ isActive }) => (
                            <>
                                {isActive && (
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-nanonetsBlue rounded-r transition-all duration-200" />
                                )}
                                <ListItemButton
                                    className={clsx(
                                        "transition-all duration-200 hover:bg-backgroundBlueLight",
                                        isActive && "!bg-backgroundBlue"
                                    )}
                                    sx={{
                                        minHeight: 40,
                                        justifyContent: open ? 'initial' : 'center',
                                        px: open ? 2 : 1,
                                        py: 1.5,
                                        borderRadius: open ? '0 8px 8px 0' : '8px',
                                        marginLeft: open ? (isActive ? '8px' : '0') : '0',
                                        marginRight: open ? '8px' : '0',
                                        marginX: open ? undefined : 'auto',
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: open ? 2 : 'auto',
                                            justifyContent: 'center',
                                            color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                            transition: 'color 0.2s',
                                        }}
                                    >
                                        <ControlPoint fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText 
                                        secondary={'Create New'} 
                                        sx={{ 
                                            opacity: open ? 1 : 0,
                                            transition: 'opacity 0.2s',
                                            '& .MuiListItemText-secondary': {
                                                fontSize: '0.75rem',
                                                fontWeight: isActive ? 600 : 400,
                                                color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                                transition: 'all 0.2s',
                                            }
                                        }} 
                                    />
                                </ListItemButton>
                            </>
                        )}
                    </NavLink>
                    <NavLink
                        to="/myWorkflows"
                        className={({ isActive }) => clsx(
                            "block relative transition-all duration-200",
                            isActive && "bg-backgroundBlue"
                        )}
                    >
                        {({ isActive }) => (
                            <>
                                {isActive && (
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-nanonetsBlue rounded-r transition-all duration-200" />
                                )}
                                <ListItemButton
                                    className={clsx(
                                        "transition-all duration-200 hover:bg-backgroundBlueLight",
                                        isActive && "!bg-backgroundBlue"
                                    )}
                                    sx={{
                                        minHeight: 40,
                                        justifyContent: open ? 'initial' : 'center',
                                        px: open ? 2 : 1,
                                        py: 1.5,
                                        borderRadius: open ? '0 8px 8px 0' : '8px',
                                        marginLeft: open ? (isActive ? '8px' : '0') : '0',
                                        marginRight: open ? '8px' : '0',
                                        marginX: open ? undefined : 'auto',
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: open ? 2 : 'auto',
                                            justifyContent: 'center',
                                            color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                            transition: 'color 0.2s',
                                        }}
                                    >
                                        <LayersOutlined fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText 
                                        secondary={'My Workflows'} 
                                        sx={{ 
                                            opacity: open ? 1 : 0,
                                            transition: 'opacity 0.2s',
                                            '& .MuiListItemText-secondary': {
                                                fontSize: '0.75rem',
                                                fontWeight: isActive ? 600 : 400,
                                                color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                                transition: 'all 0.2s',
                                            }
                                        }} 
                                    />
                                </ListItemButton>
                            </>
                        )}
                    </NavLink>
                    {/* Workflow-specific menu items grouped together */}
                    {(location.state?.service || isWorkflowOpen) && (
                        <div className={clsx(
                            "mt-2 space-y-1 overflow-hidden transition-all duration-300 ease-in-out",
                            isWorkflowOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                        )}>
                            {open && (
                                <div className={clsx(
                                    "px-4 py-2 transition-all duration-300 ease-in-out",
                                    isWorkflowOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
                                )}>
                                    <div className="text-2xs text-greyishBlue font-medium uppercase tracking-wider">
                                        Workflow
                                    </div>
                                </div>
                            )}
                            <NavLink
                                to={`/workflow/setup/${workflowId}`}
                                className={({ isActive }) => clsx(
                                    "block relative transition-all duration-200",
                                    isActive && "bg-backgroundBlue",
                                    isWorkflowOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
                                )}
                                style={{
                                    transitionDelay: isWorkflowOpen ? '50ms' : '0ms'
                                }}
                            >
                                {({ isActive }) => (
                                    <>
                                        {isActive && (
                                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-nanonetsBlue rounded-r transition-all duration-200" />
                                        )}
                                        <ListItemButton
                                            className={clsx(
                                                "transition-all duration-200 hover:bg-backgroundBlueLight",
                                                isActive && "!bg-backgroundBlue"
                                            )}
                                            sx={{
                                                minHeight: 36,
                                                justifyContent: open ? 'initial' : 'center',
                                                px: open ? 2 : 1,
                                                pl: open ? 3.5 : 1,
                                                py: 1,
                                                borderRadius: open ? '0 8px 8px 0' : '8px',
                                                marginLeft: open ? (isActive ? '8px' : '0') : '0',
                                                marginRight: open ? '8px' : '0',
                                                marginX: open ? undefined : 'auto',
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 0,
                                                    mr: open ? 2 : 'auto',
                                                    justifyContent: 'center',
                                                    color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                                    transition: 'color 0.2s',
                                                }}
                                            >
                                                <Update fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText 
                                                secondary={'Setup'} 
                                                sx={{ 
                                                    opacity: open ? 1 : 0,
                                                    transition: 'opacity 0.2s',
                                                    '& .MuiListItemText-secondary': {
                                                        fontSize: '0.75rem',
                                                        fontWeight: isActive ? 600 : 400,
                                                        color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                                        transition: 'all 0.2s',
                                                    }
                                                }} 
                                            />
                                        </ListItemButton>
                                    </>
                                )}
                            </NavLink>
                            
                            {isWorkflowOpen && (
                                <>
                                    <NavLink
                                        to={`/workflow/history/${workflowId}`}
                                        className={({ isActive }) => clsx(
                                            "block relative",
                                            isActive && "bg-backgroundBlue"
                                        )}
                                        style={{
                                            animation: 'fadeInUp 0.3s ease-out 0.1s forwards',
                                            opacity: 0
                                        }}
                                    >
                                        {({ isActive }) => (
                                            <>
                                                {isActive && (
                                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-nanonetsBlue rounded-r transition-all duration-200" />
                                                )}
                                                <ListItemButton
                                                    className={clsx(
                                                        "transition-all duration-200 hover:bg-backgroundBlueLight",
                                                        isActive && "!bg-backgroundBlue"
                                                    )}
                                                    sx={{
                                                        minHeight: 36,
                                                        justifyContent: open ? 'initial' : 'center',
                                                        px: open ? 2 : 1,
                                                        pl: open ? 3.5 : 1,
                                                        py: 1,
                                                        borderRadius: open ? '0 8px 8px 0' : '8px',
                                                        marginLeft: open ? (isActive ? '8px' : '0') : '0',
                                                        marginRight: open ? '8px' : '0',
                                                        marginX: open ? undefined : 'auto',
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={{
                                                            minWidth: 0,
                                                            mr: open ? 2 : 'auto',
                                                            justifyContent: 'center',
                                                            color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                                            transition: 'color 0.2s',
                                                        }}
                                                    >
                                                        <Layers fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        secondary={`Run history (${runCount})`} 
                                                        sx={{ 
                                                            opacity: open ? 1 : 0,
                                                            transition: 'opacity 0.2s',
                                                            '& .MuiListItemText-secondary': {
                                                                fontSize: '0.75rem',
                                                                fontWeight: isActive ? 600 : 400,
                                                                color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                                                transition: 'all 0.2s',
                                                            }
                                                        }} 
                                                    />
                                                </ListItemButton>
                                            </>
                                        )}
                                    </NavLink>
                                    
                                    <NavLink
                                        to={`/workflow/dataViews/${workflowId}`}
                                        className={({ isActive }) => clsx(
                                            "block relative",
                                            isActive && "bg-backgroundBlue"
                                        )}
                                        style={{
                                            animation: 'fadeInUp 0.3s ease-out 0.15s forwards',
                                            opacity: 0
                                        }}
                                    >
                                        {({ isActive }) => (
                                            <>
                                                {isActive && (
                                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-nanonetsBlue rounded-r transition-all duration-200" />
                                                )}
                                                <ListItemButton
                                                    className={clsx(
                                                        "transition-all duration-200 hover:bg-backgroundBlueLight",
                                                        isActive && "!bg-backgroundBlue"
                                                    )}
                                                    sx={{
                                                        minHeight: 36,
                                                        justifyContent: open ? 'initial' : 'center',
                                                        px: open ? 2 : 1,
                                                        pl: open ? 3.5 : 1,
                                                        py: 1,
                                                        borderRadius: open ? '0 8px 8px 0' : '8px',
                                                        marginLeft: open ? (isActive ? '8px' : '0') : '0',
                                                        marginRight: open ? '8px' : '0',
                                                        marginX: open ? undefined : 'auto',
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={{
                                                            minWidth: 0,
                                                            mr: open ? 2 : 'auto',
                                                            justifyContent: 'center',
                                                            color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                                            transition: 'color 0.2s',
                                                        }}
                                                    >
                                                        <Dataset fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        secondary={'Data views'} 
                                                        sx={{ 
                                                            opacity: open ? 1 : 0,
                                                            transition: 'opacity 0.2s',
                                                            '& .MuiListItemText-secondary': {
                                                                fontSize: '0.75rem',
                                                                fontWeight: isActive ? 600 : 400,
                                                                color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                                                transition: 'all 0.2s',
                                                            }
                                                        }} 
                                                    />
                                                </ListItemButton>
                                            </>
                                        )}
                                    </NavLink>
                                </>
                            )}
                        </div>
                    )}
                    {/* User section with submenu */}
                    <div className="absolute bottom-0 left-0 right-0 pb-6 pt-2 flex flex-col-reverse">
                        <div className="relative px-2">
                            <ListItemButton 
                                onClick={() => setUserOpen(!userOpen)}
                                className="transition-all duration-200 rounded-lg shadow-lg border-2  bg-blue-500 "
                                sx={{
                                    minHeight: 48,
                                    justifyContent: open ? 'initial' : 'center',
                                    px: open ? 2 : 1.5,
                                    py: 1.5,
                                    borderRadius: '12px',
                                    marginX: open ? '0' : 'auto',
                                    width: open ? 'auto' : '48px',
                                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                                    backgroundColor: 'backgroundBlue',
                                    '&:hover': {
                                        backgroundColor: 'backgroundBlue',
                                    }
                                }}
                            >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: open ? 1 : 'auto',
                                    justifyContent: 'center',
                                    color: 'greyishBlue',
                                    transition: 'color 0.2s',
                                }}
                            >
                                <Person fontSize="small" />
                            </ListItemIcon>
                            <ListItemText 
                                secondary={me?.email || 'User'} 
                                sx={{ 
                                    opacity: open ? 1 : 0,
                                    transition: 'opacity 0.2s',
                                    '& .MuiListItemText-secondary': {
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        color: 'nanonetsBlue',
                                    }

                                }} 
                            />
                            {open && (
                                <div className="ml-1 flex-shrink-0">
                                    <div className="w-6 h-6 rounded-full bg-white shadow-md flex items-center justify-center border border-borderGrey px-0">
                                        <ExpandMore 
                                            sx={{ 
                                                transform: userOpen ? 'rotate(0deg)' : 'rotate(180deg)', 
                                                transition: 'transform 0.3s ease-in-out',
                                                fontSize: '0.875rem',
                                                color: 'greyishBlue',
                                            }} 
                                        />
                                    </div>
                                </div>
                            )}
                        </ListItemButton>
                        
                        {/* User submenu items */}
                        <div className={clsx(
                            "overflow-hidden transition-all duration-300 ease-in-out absolute bottom-full left-0 right-0 mb-2",
                            userOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0 pointer-events-none"
                        )}>
                            <div className="py-1 bg-white rounded-lg shadow-lg border border-borderGrey mx-2">
                                <NavLink
                                    to="/apiKeys"
                                    className={({ isActive }) => clsx(
                                        "block relative transition-all duration-200",
                                        isActive && "bg-backgroundBlue"
                                    )}
                                >
                                    {({ isActive }) => (
                                        <>
                                            {isActive && (
                                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-nanonetsBlue rounded-r transition-all duration-200" />
                                            )}
                                            <ListItemButton
                                                className={clsx(
                                                    "transition-all duration-200 hover:bg-backgroundBlueLight",
                                                    isActive && "!bg-backgroundBlue"
                                                )}
                                                sx={{
                                                    minHeight: 36,
                                                    justifyContent: open ? 'initial' : 'center',
                                                    px: open ? 2 : 1,
                                                    pl: open ? 3.5 : 1,
                                                    py: 1,
                                                    borderRadius: open ? '0 8px 8px 0' : '8px',
                                                    marginLeft: open ? (isActive ? '8px' : '0') : '0',
                                                    marginRight: open ? '8px' : '0',
                                                    marginX: open ? undefined : 'auto',
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        minWidth: 0,
                                                        width: open ? 24 : 'auto',
                                                        mr: open ? 2 : 'auto',
                                                        justifyContent: 'center',
                                                        color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                                        transition: 'color 0.2s',
                                                    }}
                                                >
                                                    <VpnKey fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    secondary={'API Keys'} 
                                                    sx={{ 
                                                        opacity: open ? 1 : 0,
                                                        transition: 'opacity 0.2s',
                                                        '& .MuiListItemText-secondary': {
                                                            fontSize: '0.75rem',
                                                            fontWeight: isActive ? 600 : 400,
                                                            color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                                            transition: 'all 0.2s',
                                                        }
                                                    }} 
                                                />
                                            </ListItemButton>
                                        </>
                                    )}
                                </NavLink>
                                
                                <NavLink
                                    to="/admin/tokens"
                                    className={({ isActive }) => clsx(
                                        "block relative transition-all duration-200",
                                        isActive && "bg-backgroundBlue"
                                    )}
                                >
                                    {({ isActive }) => (
                                        <>
                                            {isActive && (
                                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-nanonetsBlue rounded-r transition-all duration-200" />
                                            )}
                                            <ListItemButton
                                                className={clsx(
                                                    "transition-all duration-200 hover:bg-backgroundBlueLight",
                                                    isActive && "!bg-backgroundBlue"
                                                )}
                                                sx={{
                                                    minHeight: 36,
                                                    justifyContent: open ? 'initial' : 'center',
                                                    px: open ? 2 : 1,
                                                    pl: open ? 3.5 : 1,
                                                    py: 1,
                                                    borderRadius: open ? '0 8px 8px 0' : '8px',
                                                    marginLeft: open ? (isActive ? '8px' : '0') : '0',
                                                    marginRight: open ? '8px' : '0',
                                                    marginX: open ? undefined : 'auto',
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        minWidth: 0,
                                                        width: open ? 24 : 'auto',
                                                        mr: open ? 2 : 'auto',
                                                        justifyContent: 'center',
                                                        color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                                        transition: 'color 0.2s',
                                                    }}
                                                >
                                                    <AdminPanelSettings fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    secondary={'Tokens'} 
                                                    sx={{ 
                                                        opacity: open ? 1 : 0,
                                                        transition: 'opacity 0.2s',
                                                        '& .MuiListItemText-secondary': {
                                                            fontSize: '0.75rem',
                                                            fontWeight: isActive ? 600 : 400,
                                                            color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                                            transition: 'all 0.2s',
                                                        }
                                                    }} 
                                                />
                                            </ListItemButton>
                                        </>
                                    )}
                                </NavLink>
                                
                                <NavLink
                                    to="/settings/notifications"
                                    className={({ isActive }) => clsx(
                                        "block relative transition-all duration-200",
                                        isActive && "bg-backgroundBlue"
                                    )}
                                >
                                    {({ isActive }) => (
                                        <>
                                            {isActive && (
                                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-nanonetsBlue rounded-r transition-all duration-200" />
                                            )}
                                            <ListItemButton
                                                className={clsx(
                                                    "transition-all duration-200 hover:bg-backgroundBlueLight",
                                                    isActive && "!bg-backgroundBlue"
                                                )}
                                                sx={{
                                                    minHeight: 36,
                                                    justifyContent: open ? 'initial' : 'center',
                                                    px: open ? 2 : 1,
                                                    pl: open ? 3.5 : 1,
                                                    py: 1,
                                                    borderRadius: open ? '0 8px 8px 0' : '8px',
                                                    marginLeft: open ? (isActive ? '8px' : '0') : '0',
                                                    marginRight: open ? '8px' : '0',
                                                    marginX: open ? undefined : 'auto',
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        minWidth: 0,
                                                        width: open ? 24 : 'auto',
                                                        mr: open ? 2 : 'auto',
                                                        justifyContent: 'center',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                                        transition: 'color 0.2s',
                                                    }}
                                                >
                                                    <Notifications fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    secondary={'Notification Settings'} 
                                                    sx={{ 
                                                        opacity: open ? 1 : 0,
                                                        transition: 'opacity 0.2s',
                                                        '& .MuiListItemText-secondary': {
                                                            fontSize: '0.75rem',
                                                            fontWeight: isActive ? 600 : 400,
                                                            color: isActive ? 'nanonetsBlue' : 'greyishBlue',
                                                            transition: 'all 0.2s',
                                                        }
                                                    }} 
                                                />
                                            </ListItemButton>
                                        </>
                                    )}
                                </NavLink>

                                <ListItemButton 
                                    onClick={logout}
                                    className="transition-all duration-200 hover:bg-red-50"
                                    sx={{
                                        minHeight: 36,
                                        justifyContent: open ? 'initial' : 'center',
                                        px: open ? 2 : 1,
                                        pl: open ? 3.5 : 1,
                                        py: 1,
                                        borderRadius: open ? '0 8px 8px 0' : '8px',
                                        marginX: open ? '8px' : 'auto',
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            width: open ? 24 : 'auto',
                                            mr: open ? 2 : 'auto',
                                            justifyContent: 'center',
                                            color: '#C05353',
                                            transition: 'color 0.2s',
                                        }}
                                    >
                                        <Logout fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText 
                                        secondary={'Logout'} 
                                        sx={{ 
                                            opacity: open ? 1 : 0,
                                            transition: 'opacity 0.2s',
                                            '& .MuiListItemText-secondary': {
                                                fontSize: '0.75rem',
                                                fontWeight: 400,
                                                color: '#C05353',
                                            }
                                        }} 
                                    />
                                </ListItemButton>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
            </div>
        </Drawer>
    )
}

export default Navbar;
